import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { paymentsAPI } from '../lib/api';
import { motion } from 'framer-motion';
import { Eye, EyeOff, ArrowLeft, CreditCard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      // Step 1: Create account
      await register(name, email, password);

      // Step 2: Immediately redirect to Stripe Payment Link (5-day free trial baked in)
      const trialLink = `https://buy.stripe.com/14AaEWbmp06zgyU2873AY02?prefilled_email=${encodeURIComponent(email)}`;
      window.location.href = trialLink;
      return;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#F8FAFC] flex flex-col px-6 safe-top">
      <div className="pt-4">
        <button onClick={() => navigate('/')} className="text-[#64748B] hover:text-[#0A2242] transition-colors p-1" data-testid="back-to-landing">
          <ArrowLeft size={22} />
        </button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-1 flex flex-col justify-center max-w-sm mx-auto w-full"
      >
        <div className="mb-6 flex flex-col items-center">
          <img src="/logo-coach-phone.png" alt="Elev802 Connected" className="h-20 w-auto mb-3 rounded-xl" />
          <h2 className="font-['Barlow_Condensed'] text-3xl font-bold tracking-tight text-[#0A2242]" data-testid="register-title">JOIN FOUNDERS</h2>
          <p className="text-[#64748B] text-sm mt-1">5 days free, then $14.99/month. Cancel anytime.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#64748B] font-bold mb-1.5 block">Full Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-white border border-[#E2E8F0] rounded-lg px-4 py-3 text-[#0A2242] focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent placeholder:text-[#94A3B8] transition-all"
              placeholder="Your name"
              required
              data-testid="register-name"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#64748B] font-bold mb-1.5 block">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white border border-[#E2E8F0] rounded-lg px-4 py-3 text-[#0A2242] focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent placeholder:text-[#94A3B8] transition-all"
              placeholder="you@email.com"
              required
              data-testid="register-email"
            />
          </div>
          <div className="relative">
            <label className="text-xs uppercase tracking-widest text-[#64748B] font-bold mb-1.5 block">Password</label>
            <input
              type={showPass ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white border border-[#E2E8F0] rounded-lg px-4 py-3 text-[#0A2242] focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent placeholder:text-[#94A3B8] transition-all pr-12"
              placeholder="Min 6 characters"
              required
              data-testid="register-password"
            />
            <button
              type="button"
              onClick={() => setShowPass(!showPass)}
              className="absolute right-3 top-[34px] text-[#64748B] hover:text-[#0A2242] transition-colors"
            >
              {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#0A2242] text-white font-black uppercase tracking-wide rounded-lg px-8 py-4 transition-all hover:bg-[#0E2D5A] disabled:opacity-50 mt-3 flex items-center justify-center gap-2 text-sm"
            data-testid="register-submit"
          >
            {loading ? (
              <><Loader2 size={16} className="animate-spin" /> Setting up...</>
            ) : (
              <><CreditCard size={16} /> Subscribe & Start Training</>
            )}
          </button>
        </form>

        <p className="text-center text-xs text-[#94A3B8] mt-3">
          Secure payment via Stripe. Cancel anytime.
        </p>

        <p className="text-center text-sm text-[#64748B] mt-4">
          Already a member?{' '}
          <Link to="/login" className="text-[#2563EB] hover:text-[#3B82F6] font-semibold">Sign In</Link>
        </p>
      </motion.div>
    </div>
  );
}
